/**
 * 点拖拽策略。
 * 职责：
 * 1. 实现草图点对象的拖拽行为；
 * 2. 通过射线与参考平面求交计算拖拽位置；
 * 3. 带阈值过滤微小抖动，避免不必要的坐标更新。
 */

import * as THREE from 'three';
import {
    isSketchCircleObject,
    isSketchArcObject,
    isSketchEllipseObject,
    isSketchLineObject,
    isSketchCollectionObject,
    isAbstractSketchEdgeObject,
    isSketchSlotObject,
    SketchCollectionCreationSource,
    type SketchCollectionObject,
    type AbstractSketchEdgeObject,
    SketchLineObject,
    SketchPointObject,
    SketchPointBoxSelectionRole,
} from '../../mesh/sketch';
import type {Viewer} from '../../viewer/Viewer';
import type {RawPointerEventPayload} from '../../viewer/moudles/EventManager';
import {AbstractEntityDragStrategy, EntityDragStrategyType} from './AbstractEntityDragStrategy';
import type {SketchParametricDragSession} from '../../sketch/solver/runtime/SketchParametricDragSession';
import {isPointCoincidentConstraint} from '../../sketch/solver/constraint/relationDefinition';

/**
 * 点拖拽坐标更新阈值。
 */
const POINT_DRAG_EPSILON: number = 1e-7;

//------ 矩形构造线中心点留影 开始------
/**
 * 矩形构造线几何数据片段。
 */
interface RectangleConstructionLineGeometryData {
    /** 矩形构造线数据。 */
    constructionLines?: {
        /** 构造线辅助点对象 ID 列表。 */
        pointIds?: readonly unknown[];
    };
}
//------ 矩形构造线中心点留影 结束------

/**
 * 判断对象是否为点拖拽策略。
 * @param value 待判断的拖拽策略对象。
 * @returns 当对象为点拖拽策略时返回 true。
 */
export function isPointDragStrategy(value: AbstractEntityDragStrategy): value is PointDragStrategy {
    return value.strategyType === EntityDragStrategyType.Point;
}

/**
 * 点拖拽策略。
 */
export class PointDragStrategy extends AbstractEntityDragStrategy {
    /** 策略类型标识。 */
    public readonly strategyType = EntityDragStrategyType.Point;

    /** 当前拖拽中的点对象。 */
    private readonly point: SketchPointObject;

    /** 点拖拽参考平面。 */
    private dragPlane: THREE.Plane | null = null;

    /** 点拖拽最后一次鼠标目标，供节流后的 mouseup 终局求解。 */
    private readonly requestedWorldPosition = new THREE.Vector3();

    /** 是否已经记录点拖拽目标。 */
    private hasRequestedWorldPosition = false;

    /** 当前移动域中禁止作为点捕捉目标的对象 ID 集合。 */
    private snapExcludedTargetObjectIds: ReadonlySet<string> = new Set();

    /** 当前拖动端点所连接直线的其它端点对象集合。 */
    private directionalSnapReferencePoints: readonly SketchPointObject[] = [];

    /** 复用的方向参考点世界坐标，避免 pointermove 热路径重复创建向量。 */
    private directionalSnapReferenceWorldPositions: readonly THREE.Vector3[] = [];

    /** 当前最终目标是否属于多边形整体参数化拖拽。 */
    private isPolygonParametricDrag = false;

    /** 当前最终目标是否属于曲线刚体控制点参数化拖拽。 */
    private isRigidCurveParametricDrag = false;

    /** pointerdown 时捕获基线的 FreeCAD 式参数拖拽会话。 */
    private parametricDragSession: SketchParametricDragSession | null = null;

    /**
     * 构造函数。
     * @param renderer 渲染器实例。
     * @param point 当前拖拽的点对象。
     */
    constructor(renderer: Viewer, point: SketchPointObject) {
        super(renderer);
        this.point = point;
    }

    /**
     * 获取当前拖拽的点对象。
     * @returns 拖拽中的点对象。
     */
    public getPoint(): SketchPointObject {
        return this.point;
    }

    /**
     * 开始点拖拽。
     * 初始化参考平面并启动留影。
     */
    public start(payload: RawPointerEventPayload): void {
        void payload;
        this.resetAdaptiveParametricPreviewState();
        this.hasRequestedWorldPosition = false;
        this.snapExcludedTargetObjectIds = new Set();
        this.directionalSnapReferencePoints = [];
        this.directionalSnapReferenceWorldPositions = [];
        this.isPolygonParametricDrag = false;
        this.isRigidCurveParametricDrag = false;
        this.parametricDragSession?.dispose();
        this.parametricDragSession = null;
        // 解析留影承载对象并启动留影。
        const previewOwner = this.resolveDragPreviewOwner();
        this.dragPreviewOwner = previewOwner;
        this.snapExcludedTargetObjectIds = this.renderer.pointSnapManager
            ?.createExcludedTargetObjectIds([previewOwner]) ?? new Set([this.point.uuid]);
        this.directionalSnapReferencePoints = this.resolveDirectionalSnapReferencePoints();
        this.directionalSnapReferenceWorldPositions = this.directionalSnapReferencePoints
            .map(() => new THREE.Vector3());
        this.beginSketchRuntimeHistorySession('移动草图点', 'sketch.runtime.drag.point');
        /**
         * 点是本次直接操作目标，留影宿主则承载槽口、集合和曲线的实体级关系。
         * 两者必须进入同一参数岛，否则只从子点出发会漏掉根对象上的 Symmetric/FixSlot。
         */
        const parametricDragSeedObjects = previewOwner === this.point
            ? [this.point]
            : [previewOwner, this.point];
        this.parametricDragSession = this.renderer.beginActiveSketchParametricDragSession(
            parametricDragSeedObjects,
        );
        if (!this.parametricDragSession) {
            this.cancelSketchRuntimeHistorySession();
            this.dragPreviewOwner = null;
            return;
        }
        try {
            this.invokeDragPreviewBegin(previewOwner);
        } catch (error) {
            this.cancelSketchRuntimeHistorySession();
            this.parametricDragSession?.dispose();
            this.parametricDragSession = null;
            this.dragPreviewOwner = null;
            throw error;
        }

        // 构建拖拽参考平面，用于后续射线求交。
        this.dragPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(
            this.point.referencePlane.normal.clone(),
            this.point.getPosition()
        );

        // 草图点拖拽期间禁用相机控制，避免左键拖拽触发视图平移。
        this.disableCameraControlsForDrag();
    }

    /**
     * 更新点拖拽坐标。
     * @param payload 原始鼠标事件载荷。
     */
    public update(payload: RawPointerEventPayload): void {
        if (!this.dragPlane) {
            return;
        }

        // 左键释放时立即终止拖拽。
        if ((payload.buttons & 1) !== 1) {
            return;
        }

        const camera = this.renderer.camera;
        if (!camera) {
            return;
        }

        // 屏幕坐标转 NDC。
        if (!this.updateMouseNdc(payload.clientX, payload.clientY)) {
            return;
        }

        // 射线与参考平面求交。
        this.raycaster.setFromCamera(this.mouseNdc, camera);
        const intersection = new THREE.Vector3();
        if (!this.raycaster.ray.intersectPlane(this.dragPlane, intersection)) {
            return;
        }

        // 捕捉只替换本帧的瞬态鼠标目标，不创建关系，也不改变参数拖拽会话的提交边界。
        const requestedPosition = this.resolvePointDragTarget(intersection, payload);

        //------ 多边形集合刚体拖拽 开始------
        const polygonCollection = this.resolvePolygonCollectionOwner();
        if (polygonCollection) {
            this.requestedWorldPosition.copy(requestedPosition);
            this.hasRequestedWorldPosition = true;
            this.isPolygonParametricDrag = true;
            this.isRigidCurveParametricDrag = false;
            this.runAdaptiveParametricPreview(() => (
                this.handlePolygonCollectionPointDrag(polygonCollection, this.requestedWorldPosition)
            ));
            payload.nativeEvent.preventDefault();
            return;
        }
        //------ 多边形集合刚体拖拽 结束------

        // 普通点与槽口控制点都不先写运行时几何；鼠标位置直接编译为会话内瞬态目标。
        this.requestedWorldPosition.copy(requestedPosition);
        this.hasRequestedWorldPosition = true;
        this.isPolygonParametricDrag = false;
        this.isRigidCurveParametricDrag = this.resolveRigidCurveDragOwner() !== null;
        const applied = this.runAdaptiveParametricPreview(() => (
            this.isRigidCurveParametricDrag
                ? this.previewRigidCurveControlPointDrag(this.requestedWorldPosition) === true
                : this.renderer.previewSketchParametricPointDrag(
                    this.point,
                    this.requestedWorldPosition,
                    this.parametricDragSession ?? undefined,
                )
        ));

        if (applied === true) {
            this.markGeometryChanged();
        }

        payload.nativeEvent.preventDefault();
    }

    /**
     * 子类具体停止逻辑。
     */
    protected doStop(): void {
        try {
            // 外部事务已替换草图时，旧基线不再具备写回 authority，必须直接放弃整个手势。
            if (this.discardStaleSketchDrag(this.parametricDragSession)) {
                return;
            }
            /**
             * mouseup 没有新坐标：优先补算被节流的最终请求；已执行成功且草图 revision
             * 未变化时直接复用；普通失败帧可重新验证，stale 帧已在上方永久拒绝。
             */
            if (this.hasRequestedWorldPosition) {
                let applied = this.flushPendingParametricPreview();
                if (applied === null && !this.parametricDragSession?.canReuseLastAppliedFrame()) {
                    const polygonCollection = this.isPolygonParametricDrag
                        ? this.resolvePolygonCollectionOwner()
                        : null;
                    applied = polygonCollection
                        ? this.handlePolygonCollectionPointDrag(polygonCollection, this.requestedWorldPosition)
                        : this.isRigidCurveParametricDrag
                            ? this.previewRigidCurveControlPointDrag(this.requestedWorldPosition) === true
                            : this.renderer.previewSketchParametricPointDrag(
                                this.point,
                                this.requestedWorldPosition,
                                this.parametricDragSession ?? undefined,
                            );
                }

                if (applied === true) {
                    this.markGeometryChanged();
                }
            }

            // pending 补帧也可能观察到外部版本变化，提交历史前必须再次确认 authority。
            if (this.discardStaleSketchDrag(this.parametricDragSession)) {
                return;
            }
            this.commitSketchRuntimeHistorySession();
        } finally {
            this.parametricDragSession?.dispose();
            this.parametricDragSession = null;
            this.renderer.pointSnapManager?.clearPointerSnapFeedback();
            this.dragPlane = null;
            this.hasRequestedWorldPosition = false;
            this.snapExcludedTargetObjectIds = new Set();
            this.directionalSnapReferencePoints = [];
            this.directionalSnapReferenceWorldPositions = [];
            this.isPolygonParametricDrag = false;
            this.isRigidCurveParametricDrag = false;
            this.resetAdaptiveParametricPreviewState();
        }
    }

    /**
     * 解析当前拖动帧应交给参数求解器的最终目标坐标。
     *
     * 点捕捉只影响瞬态坐标：即使结果携带 `suggestedRelationKind`，本策略也不会读取或调用
     * 几何关系创建服务。按住 Ctrl 时依据 SolidWorks 拖动端点语义抑制推理与捕捉预览，直接
     * 使用草图平面自由投影点。
     *
     * @param freePosition 鼠标射线在草图平面上的自由投影点。
     * @param payload 当前鼠标事件载荷。
     * @returns 捕捉后的坐标；未命中或 Ctrl 抑制时返回自由投影点。
     */
    private resolvePointDragTarget(
        freePosition: THREE.Vector3,
        payload: RawPointerEventPayload,
    ): THREE.Vector3 {
        const pointSnapManager = this.renderer.pointSnapManager;
        if (!pointSnapManager) {
            return freePosition;
        }

        if (payload.ctrlKey) {
            pointSnapManager.clearPointerSnapFeedback();
            return freePosition;
        }

        // 方向参考点对象在约束求解后可能移动，每帧只复用向量并同步最新位置。
        this.directionalSnapReferencePoints.forEach((referencePoint, index) => {
            this.directionalSnapReferenceWorldPositions[index]?.copy(referencePoint.position);
        });

        const result = pointSnapManager.resolvePointerSnap(
            payload.clientX,
            payload.clientY,
            freePosition,
            this.snapExcludedTargetObjectIds,
            this.directionalSnapReferenceWorldPositions,
        );
        return result?.position ?? freePosition;
    }

    /**
     * 收集拖动直线端点对应的方向参考点。
     *
     * 一个共享端点可能连接多条直线，因此必须保留每条直线的另一端点，由水平/竖直策略
     * 根据当前鼠标屏幕距离选择最接近的一项。拖动中点或非直线控制点时返回空集合。
     *
     * @returns 已按对象 UUID 去重的其它端点集合。
     */
    private resolveDirectionalSnapReferencePoints(): readonly SketchPointObject[] {
        const referencePoints = new Map<string, SketchPointObject>();
        this.resolveLinkedLines().forEach((line) => {
            const referencePoint = line.startPoint === this.point
                ? line.endPoint
                : line.endPoint === this.point
                    ? line.startPoint
                    : null;
            if (referencePoint) {
                referencePoints.set(referencePoint.uuid, referencePoint);
            }
        });
        return Array.from(referencePoints.values());
    }

    /**
     * 从点对象向上解析拖拽留影承载对象。
     * 优先级：
     * 1. 点祖先链中的槽口或多边形复合对象；
     * 2. 被结构数据追踪的矩形复合对象；
     * 3. 点关联线所属的封闭线段几何对象；
     * 4. 点关联线对象；
     * 5. 点关联的边对象（圆/圆弧/椭圆等）；
     * 6. 点对象自身。
     * @returns 应承载留影的最上层关联对象。
     */
    private resolveDragPreviewOwner(): THREE.Object3D {
        //------ 槽口留影承载识别 开始------
        // 优先沿祖先链查找 SketchSlotObject，槽口作为整体承载留影。
        let ancestor: THREE.Object3D | null = this.point;
        while (ancestor) {
            if (isSketchSlotObject(ancestor)) {
                return ancestor;
            }
            ancestor = ancestor.parent;
        }
        //------ 槽口留影承载识别 结束------

        //------ 多边形留影承载识别 开始------
        const polygonCollection = this.resolvePolygonCollectionOwner();
        if (polygonCollection) {
            /**
             * 多边形辅助圆心是圆对象的子点，但拖动更新会整体平移多边形外框和辅助圆。
             * 留影宿主必须与实际几何更新宿主一致，不能在后续关联边解析中提前停在辅助圆。
             */
            return polygonCollection;
        }
        //------ 多边形留影承载识别 结束------

        //------ 矩形构造线中心点留影 开始------
        const rectangleCollection = this.resolveRectangleCollectionOwnerFromTrackedConstructionPoint();
        if (rectangleCollection) {
            return rectangleCollection;
        }
        //------ 矩形构造线中心点留影 结束------

        const linkedLines = this.resolveLinkedLines();
        let fallbackOwner: THREE.Object3D | null = null;

        for (const line of linkedLines) {
            const topOwner = this.resolveTopPreviewOwnerFromLine(line);
            if (isSketchCollectionObject(topOwner)) {
                return topOwner;
            }
            if (!fallbackOwner) {
                fallbackOwner = topOwner;
            }
        }

        if (fallbackOwner) {
            return fallbackOwner;
        }

        // 查找关联的边对象（圆/圆弧/椭圆等非线段类型）。
        const linkedEdge = this.resolveLinkedEdgeObject();
        if (linkedEdge) {
            return linkedEdge;
        }

        return this.point;
    }

    //------ 矩形构造线中心点留影 开始------
    /**
     * 从矩形构造线中心控制点反向解析所属矩形集合。
     *
     * 设计说明：
     * 1. 中心矩形的交点控制点不是外框线或构造线端点，无法通过 `connectedEdges` 找到闭合集合；
     * 2. 正式创建矩形时已在集合 `geometryData.constructionLines.pointIds` 中记录该辅助点；
     * 3. 拖拽留影只需要解析视觉承载对象，因此在当前对象树内按该稳定记录反查矩形集合。
     *
     * @returns 所属矩形集合；未命中时返回 null。
     */
    private resolveRectangleCollectionOwnerFromTrackedConstructionPoint(): SketchCollectionObject | null {
        if (this.point.boxSelectionRole !== SketchPointBoxSelectionRole.RectangleConstructionIntersection) {
            return null;
        }

        const searchRoot = this.resolveDragPreviewSearchRoot();
        let matchedCollection: SketchCollectionObject | null = null;

        searchRoot.traverse((object) => {
            if (matchedCollection) {
                return;
            }
            if (!isSketchCollectionObject(object)) {
                return;
            }
            if (object.creationSource !== SketchCollectionCreationSource.Rectangle) {
                return;
            }
            if (!this.isPointTrackedByRectangleConstructionData(object)) {
                return;
            }

            // 命中记录当前中心控制点的矩形集合后，直接作为拖拽留影承载对象。
            matchedCollection = object;
        });

        return matchedCollection;
    }

    /**
     * 解析拖拽留影反查使用的对象树根节点。
     *
     * @returns 当前点所属对象树根节点。
     */
    private resolveDragPreviewSearchRoot(): THREE.Object3D {
        let current: THREE.Object3D | null = this.point;
        let fallbackRoot: THREE.Object3D = this.point;

        while (current) {
            if (Reflect.get(current, 'isSketchGroup') === true) {
                return current;
            }
            fallbackRoot = current;
            current = current.parent;
        }

        // 历史测试或脱离草图组的临时对象没有 SketchGroup，此时退回当前对象树根。
        return fallbackRoot;
    }

    /**
     * 判断矩形集合是否通过构造线辅助点记录追踪当前点。
     *
     * @param collection 候选矩形集合。
     * @returns 当前点 ID 被矩形构造线辅助点记录包含时返回 true。
     */
    private isPointTrackedByRectangleConstructionData(collection: SketchCollectionObject): boolean {
        const geometryData = collection.geometryData as RectangleConstructionLineGeometryData | null;
        const pointIds = geometryData?.constructionLines?.pointIds;
        if (!Array.isArray(pointIds)) {
            return false;
        }

        // 只接受字符串 UUID，避免历史数据中的异常值误命中当前对象。
        return pointIds.some((pointId) => pointId === this.point.uuid);
    }
    //------ 矩形构造线中心点留影 结束------

    /**
     * 收集点对象关联的线对象集合（去重）。
     * @returns 关联线对象数组。
     */
    private resolveLinkedLines(): SketchLineObject[] {
        const uniqueLines = new Map<string, SketchLineObject>();
        const edges = this.point.getConnectedEdgesSnapshot();
        edges.forEach((edge) => {
            if (isSketchLineObject(edge) && !uniqueLines.has(edge.uuid)) {
                uniqueLines.set(edge.uuid, edge);
            }
        });

        // 向上查找祖先中的线对象。
        let current: THREE.Object3D | null = this.point;
        while (current) {
            if (isSketchLineObject(current) && !uniqueLines.has(current.uuid)) {
                uniqueLines.set(current.uuid, current);
            }
            current = current.parent;
        }

        return Array.from(uniqueLines.values());
    }

    /**
     * 从点对象关联的边集合和祖先链中查找非线段类型的边对象（圆/圆弧/椭圆等）。
     * @returns 关联的边对象；未找到返回 null。
     */
    private resolveLinkedEdgeObject(): AbstractSketchEdgeObject | null {
        // 从已注册的连边中查找。
        const edges = this.point.getConnectedEdgesSnapshot();
        for (const edge of edges) {
            if (isAbstractSketchEdgeObject(edge) && !isSketchLineObject(edge)) {
                return edge;
            }
        }

        // 向上查找祖先中的边对象。
        let current: THREE.Object3D | null = this.point;
        while (current) {
            if (isAbstractSketchEdgeObject(current) && !isSketchLineObject(current)) {
                return current;
            }
            current = current.parent;
        }

        return null;
    }

    //------ 多边形集合刚体拖拽 开始------
    /**
     * 解析当前点所属的多边形集合对象。
     * @returns 多边形集合对象；未命中时返回 null。
     */
    private resolvePolygonCollectionOwner(): SketchCollectionObject | null {
        let current: THREE.Object3D | null = this.point;
        while (current) {
            if (
                isSketchCollectionObject(current)
                && current.creationSource === SketchCollectionCreationSource.Polygon
            ) {
                return current;
            }
            current = current.parent;
        }
        return null;
    }

    /**
     * 解析当前点是否为曲线刚体平移手柄。
     *
     * 圆心、圆弧圆心/默认中点和椭圆中心在交互上移动整条曲线；它们不能退化成
     * 单点目标，否则运行时刷新会用未移动的其他定义点反算出错误半径或轴长。
     *
     * @returns 对应曲线对象；当前点是形状编辑手柄时返回 null。
     */
    private resolveRigidCurveDragOwner(): AbstractSketchEdgeObject | null {
        const owner = this.dragPreviewOwner ?? this.resolveDragPreviewOwner();

        if (isSketchCircleObject(owner) && this.point === owner.getCenterControlPoint()) {
            return owner;
        }
        if (isSketchArcObject(owner)
            && (this.point === owner.getCenterPoint() || this.point === owner.getMidPoint())) {
            return owner;
        }
        if (isSketchEllipseObject(owner) && this.point === owner.getCenterPoint()) {
            return owner;
        }

        return null;
    }

    /**
     * 以同一位移构造曲线全部定义点的多目标公式意图。
     *
     * @param targetWorldPosition 被拖手柄的目标世界坐标。
     * @returns 是否写回参数解；当前点不是刚体手柄时返回 null。
     */
    private previewRigidCurveControlPointDrag(targetWorldPosition: THREE.Vector3): boolean | null {
        const owner = this.resolveRigidCurveDragOwner();

        if (!owner) {
            return null;
        }

        const delta = targetWorldPosition.clone().sub(this.point.getPosition());
        if (delta.lengthSq() <= POINT_DRAG_EPSILON * POINT_DRAG_EPSILON) {
            return false;
        }

        const pointTargets = new Map<SketchPointObject, THREE.Vector3>();

        // 刚体手柄 owner 的全部定义点以同一 Δ 登记为目标（与既有行为一致）
        this.registerRigidCurveDefiningPointTargets(owner, delta, pointTargets);

        // 把刚体平移意图沿 owner 圆心上的重合关系传播到对侧刚性曲线：
        // 等距圆弧/椭圆弧/椭圆通过 PointCoincidentConstraint 与源实体绑定圆心，
        // 若只钉 owner 的定义点，对侧实体只有圆心被间接牵引，其半径/半轴等
        // 自由参数会在最小二乘解中吸收圆心位移导致变形；这里把对侧实体的
        // 全部定义点以同一 Δ 登记为目标，使其同样整体平移。
        const ownerCenterPoint = isSketchCircleObject(owner)
            ? owner.getCenterControlPoint()
            : isSketchArcObject(owner)
                ? owner.getCenterPoint()
                : isSketchEllipseObject(owner)
                    ? owner.getCenterPoint()
                    : null;
        if (ownerCenterPoint) {
            this.propagateRigidDeltaAlongCenterCoincidentRelations(
                ownerCenterPoint,
                delta,
                pointTargets,
            );
        }

        return this.renderer.previewSketchParametricPointTargets(
            pointTargets,
            `rigid-curve-point-drag:${owner.uuid}:${this.point.uuid}`,
            this.parametricDragSession ?? undefined,
        );
    }

    /**
     * 把一条刚性曲线（圆/圆弧/椭圆）的全部定义点以同一 Δ 登记为拖拽目标。
     *
     * 说明：登记口径与刚体手柄 owner 完全一致——圆登记圆心 + 圆周控制点，
     * 圆弧登记圆心 + 起止点，椭圆登记起止点 + 全部基准轴控制点；
     * 同一位移钉死全部定义点后，形状参数无需变化，实体整体平移。
     *
     * @param entity 刚性曲线实体。
     * @param delta 刚体位移。
     * @param pointTargets 已登记的点目标集合（原地追加，按 uuid 覆盖去重）。
     */
    private registerRigidCurveDefiningPointTargets(
        entity: AbstractSketchEdgeObject,
        delta: THREE.Vector3,
        pointTargets: Map<SketchPointObject, THREE.Vector3>,
    ): void {
        const register = (point: SketchPointObject): void => {
            pointTargets.set(point, point.getPosition().add(delta));
        };

        if (isSketchCircleObject(entity)) {
            register(entity.getCenterControlPoint());
            register(entity.getEdgeControlPoint());
        } else if (isSketchArcObject(entity)) {
            register(entity.getCenterPoint());
            register(entity.getStartPoint());
            register(entity.getEndPoint());
        } else if (isSketchEllipseObject(entity)) {
            register(entity.getStartPoint());
            register(entity.getEndPoint());
            entity.getCardinalControlPointsSnapshot().forEach(register);
        }
    }

    /**
     * 沿圆心上的点点重合关系递归传播刚体平移意图。
     *
     * 说明：
     * 1. 从 owner 圆心出发，遍历其 `PointCoincidentConstraint` 的全部对侧目标点；
     * 2. 仅当对侧点是某条刚性曲线（圆/圆弧/椭圆）的圆心时传播——把该曲线
     *    全部定义点以同一 Δ 登记为目标，并沿该圆心继续递归，覆盖多代等距实体；
     * 3. 对侧点是线段端点等普通共位场景时不传播：点级共位由参数方程求解器
     *    处理，不应把整条线刚性拖动；
     * 4. 使用 visited 集合防止重合关系成环时无限递归。
     *
     * @param centerPoint 当前刚体曲线的圆心控制点。
     * @param delta 刚体位移。
     * @param pointTargets 已登记的点目标集合（原地追加）。
     * @param visitedPointIds 已传播过的圆心点 uuid 集合（递归去重）。
     */
    private propagateRigidDeltaAlongCenterCoincidentRelations(
        centerPoint: SketchPointObject,
        delta: THREE.Vector3,
        pointTargets: Map<SketchPointObject, THREE.Vector3>,
        visitedPointIds: Set<string> = new Set(),
    ): void {
        // 重合关系可能成环（两圆互相重合），已处理过的圆心直接跳过
        if (visitedPointIds.has(centerPoint.uuid)) {
            return;
        }
        visitedPointIds.add(centerPoint.uuid);

        centerPoint.constraints.getRelations().forEach((relation) => {
            // 只沿点点重合关系传播，其余关系（相切/固定等）不参与刚性意图
            if (!isPointCoincidentConstraint(relation)) {
                return;
            }

            relation.getTargetsSnapshot().forEach((targetPoint) => {
                // 跳过当前圆心自身
                if (targetPoint === centerPoint) {
                    return;
                }

                targetPoint.getConnectedEdgesSnapshot().forEach((edge) => {
                    // 仅传播到"对侧点是对侧曲线圆心"的刚性曲线；
                    // 线段端点等共位点不传播刚体位移
                    const isRigidCurveCenter = (
                        (isSketchCircleObject(edge) && targetPoint === edge.getCenterControlPoint())
                        || (isSketchArcObject(edge) && targetPoint === edge.getCenterPoint())
                        || (isSketchEllipseObject(edge) && targetPoint === edge.getCenterPoint())
                    );
                    if (!isRigidCurveCenter) {
                        return;
                    }

                    this.registerRigidCurveDefiningPointTargets(edge, delta, pointTargets);
                    // 沿对侧圆心继续递归，覆盖"源 → 一代等距 → 二代等距"的链式重合
                    this.propagateRigidDeltaAlongCenterCoincidentRelations(
                        targetPoint,
                        delta,
                        pointTargets,
                        visitedPointIds,
                    );
                });
            });
        });
    }

    /**
     * 处理多边形集合中的点拖拽。
     * 说明：
     * 1. 多边形点拖拽应解释为“整组平移”，而非单点自由编辑；
     * 2. 顶点集合与辅助圆控制点按同一位移编译成瞬态方程，保证多边形形状和辅助圆半径不变；
     * 3. 该逻辑只对 `SketchCollectionCreationSource.Polygon` 生效，不影响矩形集合。
     * @param collection 多边形集合对象。
     * @param targetWorldPosition 当前鼠标对应的目标世界坐标。
     * @returns 是否完成参数解写回。
     */
    private handlePolygonCollectionPointDrag(
        collection: SketchCollectionObject,
        targetWorldPosition: THREE.Vector3,
    ): boolean {
        const delta = targetWorldPosition.clone().sub(this.point.getPosition());
        if (delta.lengthSq() <= POINT_DRAG_EPSILON * POINT_DRAG_EPSILON) {
            return false;
        }

        const uniqueVertexPointMap = new Map<string, SketchPointObject>();
        collection.getLines().forEach((source) => {
            if (isSketchLineObject(source)) {
                uniqueVertexPointMap.set(source.startPoint.uuid, source.startPoint);
                uniqueVertexPointMap.set(source.endPoint.uuid, source.endPoint);
            }
        });
        const pointTargets = new Map<SketchPointObject, THREE.Vector3>();

        uniqueVertexPointMap.forEach((point) => {
            pointTargets.set(point, point.getPosition().add(delta));
        });

        collection.children.forEach((child) => {
            if (isSketchCircleObject(child) && child.isConstruction) {
                const centerPoint = child.getCenterControlPoint();
                const edgePoint = child.getEdgeControlPoint();

                pointTargets.set(centerPoint, centerPoint.getPosition().add(delta));
                pointTargets.set(edgePoint, edgePoint.getPosition().add(delta));
            }
        });

        const applied = this.renderer.previewSketchParametricPointTargets(
            pointTargets,
            `polygon-translate:${collection.uuid}`,
            this.parametricDragSession ?? undefined,
        );

        if (!applied) {
            return false;
        }

        if (this.renderer.shadedSketchContoursEnabled && collection.isClosedGeometry) {
            collection.refreshContourGeometry();
        }

        this.markGeometryChanged();
        return true;
    }
    //------ 多边形集合刚体拖拽 结束------

    /**
     * 从线对象向上解析最上层留影对象。
     * @param line 线对象。
     * @returns 线段几何对象（若存在）或线对象本身。
     */
    private resolveTopPreviewOwnerFromLine(line: SketchLineObject): THREE.Object3D {
        // 优先使用直接关联的封闭线段几何对象。
        if (line.closedCollection && isSketchCollectionObject(line.closedCollection)) {
            return line.closedCollection;
        }

        // 向上查找祖先中的线段几何对象。
        let current: THREE.Object3D | null = line;
        while (current) {
            if (isSketchCollectionObject(current)) {
                return current;
            }
            current = current.parent;
        }

        return line;
    }

}
